import store from 'store/store';
import { getTipoDePadronPorContenType } from 'api/padrones';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import API from '../api';
import { toMoment } from './normalizers';
import configEntidad from './configEntidad';
import round from './round';

/**
 * Verifica si la entidad tiene configurado el recargo a partir del inicio del periodo
 * @returns {boolean} - true si está configurado, false en caso contrario
 */
const fechaApartirInicioPeriodo = () => {
  const config = configEntidad();
  return config?.fecha_recargo_apartir_inicio_periodo || false;
};

/**
 * Calcula la diferencia entre dos fechas
 * @param {string} date1 - Fecha inicial en formato YYYY-MM-DD
 * @param {string} date2 - Fecha final en formato YYYY-MM-DD
 * @param {string} separator - Separador de la fecha (default: '-')
 * @returns {{years: number, months: number, days: number}} - Objeto con la diferencia en años,
 *  meses y días
 */
const diffDates = (date1, date2, separator = '-') => {
  const d1 = date1.split(separator);
  const d2 = date2.split(separator);
  const date1Obj = new Date(d1[0], d1[1] - 1, d1[2]);
  const date2Obj = new Date(d2[0], d2[1] - 1, d2[2]);
  // valida si la fecha de vencimiento pasó
  if (date2Obj < date1Obj) {
    return { years: 0, months: 0, days: 0 };
  }
  const apartirDePeriodo = fechaApartirInicioPeriodo();
  // valida con dias para ser mas precisos
  let years = date2Obj.getFullYear() - date1Obj.getFullYear();
  let months = date2Obj.getMonth() - date1Obj.getMonth();
  let days = date2Obj.getDate() - date1Obj.getDate();

  if (days < 0) {
    const diasDelMesPrevio = new Date(date2Obj.getFullYear(), date2Obj.getMonth(), 0).getDate();
    months -= 1;
    days += diasDelMesPrevio;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }
  // ADJUSTMONTHS
  if (apartirDePeriodo) {
    months += 1;
  }

  return {
    years,
    months,
    days: apartirDePeriodo ? 0 : days,
  };
};

/**
 * Mapeo de periodos fiscales
 * @type {Object.<string, number>}
 */
const periodosMap = {
  DIARIO: 2,
  SEMANAL: 3,
  QUINCENAL: 4,
  MENSUAL: 5,
  BIMESTRAL: 6,
  TRIMESTRAL: 7,
  CUATRIMESTRAL: 8,
  SEMESTRAL: 9,
  ANUAL: 10,
};

/**
 * suma el porcentaje de recargo por cada periodo vencido basado en la fecha de vencimiento
 * @param {Object} params - Parámetros de entrada
 * @param {number} params.periodo - ID del periodo fiscal
 * @param {string} params.fecha_de_vencimiento - Fecha de vencimiento
 * @returns {number} - Porcentaje de recargo calculado
 */
const getPorcentajeRecargo = ({ periodo, fecha_de_vencimiento }) => {
  const now = toMoment(new Date());
  let periodos_vencidos = 0;
  const diferencia_de_fechas = diffDates(fecha_de_vencimiento, now.format('YYYY-MM-DD'));

  const diferencia_en_dias = diferencia_de_fechas.days;

  let total_meses = 0;
  let divisor = 1;

  switch (periodo) {
    case periodosMap.DIARIO:
      periodos_vencidos = diferencia_en_dias;
      break;
    case periodosMap.SEMANAL:
      periodos_vencidos = Math.floor(diferencia_en_dias / 7)
        + (diferencia_en_dias % 7 > 0 ? 1 : 0);
      break;
    case periodosMap.QUINCENAL:
      periodos_vencidos = Math.floor(diferencia_en_dias / 15)
        + (diferencia_en_dias % 15 > 0 ? 1 : 0);
      break;
    case periodosMap.MENSUAL:
      periodos_vencidos = diferencia_de_fechas.years * 12
        + diferencia_de_fechas.months;
      break;
    case periodosMap.BIMESTRAL:
    case periodosMap.TRIMESTRAL:
    case periodosMap.CUATRIMESTRAL:
    case periodosMap.SEMESTRAL:
      total_meses = diferencia_de_fechas.years * 12 + diferencia_de_fechas.months;

      if (periodo === periodosMap.BIMESTRAL) divisor = 2;
      else if (periodo === periodosMap.TRIMESTRAL) divisor = 3;
      else if (periodo === periodosMap.CUATRIMESTRAL) divisor = 4;
      else divisor = 6;

      periodos_vencidos = Math.floor(total_meses / divisor)
        + (total_meses % divisor > 0 ? 1 : 0);
      break;
    case periodosMap.ANUAL:
      periodos_vencidos = diferencia_de_fechas.years
        + (diferencia_de_fechas.months > 0 ? 1 : 0);
      break;
    default:
      break;
  }
  const start = toMoment(fecha_de_vencimiento);
  // calculamos el porcentaje de recargo con 4 decimales
  // Ese 1.47 es el porcentaje que dice el SAT/estado para calcular el recargo
  // El porcentaje anterior era del 1.47%, el nuevo porcentaje sera de 2.07%.
  // (palabras del Jair)
  // antes de 2025 se consetva el 1.47 si son a partir del 2026 será de 2.07%
  // el nuevo id del periodo sera 12 para 26
  // monto actualizado
  const porcentaje_acumulado = Array.from({ length: periodos_vencidos })
    .map((_, i) => start.clone().add(i, 'months'))
    .map((mes) => (mes.year() >= 2026 ? 2.07 : 1.47) / 100)
    .reduce((acc, val) => acc + val, 0);
  return parseFloat(porcentaje_acumulado.toFixed(4));
};

/**
 * Obtiene el INPC (Índice Nacional de Precios al Consumidor) para un periodo y mes específicos
 * @param {string} periodo - Año del periodo
 * @param {string} mes - Mes del periodo
 * @returns {Promise<Array|null>} - Resultados del INPC o null si hay error
 */
export const getINPC = async (periodo, mes) => {
  try {
    const { data } = await API.get('configuracion/inpc-public', {
      params: { periodo, mes },
    });
    return data.results;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error al obtener INPC:', error);
    return null;
  }
};

/**
 * Trunca el factor de actualización a 4 dígitos
 * @param {number} factor - Factor de actualización
 * @returns {number} - Factor de actualización truncado a 4 dígitos
 */
const truncate = (factor, digits = 4) => {
  const stringFactor = factor.toString();
  if (!stringFactor.includes('.')) return factor;
  const [integerPart, decimalPart] = stringFactor.split('.');
  return parseFloat(`${integerPart}.${decimalPart.slice(0, digits)}`);
};

/**
 * Funcion para calcular el factor de actualizacion
 * @param {Object} INPC - Datos del INPC
 * @param {string} tipoDePadron - Tipo de padron
 * @returns {number} - Factor de actualizacion
 */
const calcularFactorDeActualizacion = (INPC, tipoDePadron) => {
  // Obtenemos el INPC actual de la store
  const currentINPC = store.getState().app.lastINPC;

  // Si no existe el valor del INPC, retornamos 1 ya que se hace la division del valor
  // del INPC actual entre si mismo
  if (!INPC.valor) return 1;

  // Si el tipo de padron no es alcohol, calculamos el factor de actualizacion
  // con el INPC actual y el INPC del periodo declarado
  if (tipoDePadron !== padronesEnum.ALCOHOL) {
    return currentINPC.valor / INPC.valor;
  }

  // Si el tipo de padron es alcohol, retornamos el factor de actualizacion del INPC del SAT
  return INPC.factor_de_actualizacion_sat;
};

/**
 * Calcula la actualización total basada en el INPC
 * @param {Object} params - Parámetros de entrada
 * @param {number} params.importe_cargo - Importe del cargo
 * @param {Object} params.tipo_de_cargo - Tipo de cargo
 * @param {Object} params.INPC - Datos del INPC
 * @returns {Promise<number|null>} - Importe actualizado o null si hay error
 */
export const actualizacionTotal = async ({
  importe_cargo,
  tipo_de_cargo,
  INPC,
}) => {
  try {
    const tipoDePadron = getTipoDePadronPorContenType(tipo_de_cargo?.padron?.id);

    if (!tipo_de_cargo?.fecha_de_vencimiento) return 0;

    // El factor de actualización se calcula con el INPC de hoy y el INPC del periodo declarado
    // excepto para el alcohol, que se usa el factor del SAT
    const factorDeActualizacion = calcularFactorDeActualizacion(INPC, tipoDePadron);

    // Truncar el factor de actualización a 4 dígitos
    const factActTruncated4digits = truncate(factorDeActualizacion);

    // calcular el importe actualizado
    const importe_actualizado = importe_cargo * factActTruncated4digits;

    // obtenemos el valor de la actualizacion
    const output = importe_actualizado - importe_cargo;

    return round(output)[0];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error al calcular actualización:', error);
    return 0;
  }
};

/**
 * Calcula los recargos totales basados en el factor de vencimiento
 * @param {Object} params - Parámetros de entrada
 * @param {Object} params.tipo_de_cargo - Tipo de cargo
 * @param {number} params.importe_cargo - Importe del cargo
 * @param {string} params.fecha_de_vencimiento - Fecha de vencimiento
 * @param {number} params.base_gravada - Base gravada
 * @returns {Promise<number|null>} - Importe de recargos o null si hay error
 */
export const recargosTotal = async ({
  tipo_de_cargo,
  importe_cargo: actualizacion,
  fecha_de_vencimiento,
  base_gravada,
}) => {
  try {
    if (!fecha_de_vencimiento) return 0;

    const porcentaje = getPorcentajeRecargo({
      periodo: tipo_de_cargo?.periodo?.id,
      fecha_de_vencimiento,
    });
    const monto_actualizado = base_gravada + actualizacion;

    // calculamos el recargo con la base gravada mas la actualizacion
    const recargo = (monto_actualizado * porcentaje);

    return round(recargo)[0];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error al calcular recargos:', error);
    return 0;
  }
};
